import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { QRCodeSVG } from "qrcode.react";
import axios from "axios";
import {
  Zap, Target, Smartphone, Recycle, Wifi, Check, BellRing, Bookmark, ArrowRight,
} from "lucide-react";
import { Reveal } from "./Reveal";
import { API } from "@/lib/api";
import { IMAGES } from "@/data/content";
import { useLead } from "@/context/LeadContext";

const DEMO_CARD_URL = "https://card.chateaux.ai/c/demo-agent";
const TIMES = [0, 0.18, 0.38, 0.62, 0.9, 1];
const LOOP = { duration: 4, times: TIMES, repeat: Infinity, ease: "easeInOut" };

const CAPTIONS = [
  { icon: Zap, text: "Tap once — they get everything" },
  { icon: Target, text: "They're a lead the moment they tap" },
  { icon: Smartphone, text: "Works on every iPhone + Android since 2018" },
  { icon: Recycle, text: "Never reprint a card again" },
];

const LISTING_THUMBS = ["/staging/after-modern.png", "/staging/after-scandi.png", "/staging/after-coastal.png"];

function TapSimulation() {
  return (
    <div className="relative mx-auto w-[300px] h-[380px] flex items-center justify-center" data-testid="tap-simulation">
      {/* Lead notification from top */}
      <motion.div
        animate={{ opacity: [0, 0, 0, 0, 1, 0], y: [-16, -16, -16, -16, 0, 0] }}
        transition={LOOP}
        className="absolute top-0 z-30 flex items-center gap-2 bg-ink text-bone px-3.5 py-2 shadow-xl border border-bone/10"
      >
        <BellRing className="w-3.5 h-3.5 text-coral" />
        <span className="text-[11px] font-medium">New lead in your Chateaux inbox</span>
      </motion.div>

      {/* The NFC card (behind) */}
      <motion.div
        animate={{ opacity: [1, 1, 1, 0.4, 0.4, 1] }}
        transition={LOOP}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 rotate-[8deg] w-[220px] h-[139px] rounded-2xl bg-gradient-to-br from-[#141418] to-[#000] shadow-2xl border border-white/5"
        style={{ transformOrigin: "center" }}
      >
        <div className="absolute top-4 left-4 font-serif text-bone text-xl">Chateaux</div>
        <div className="absolute bottom-4 left-4 text-bone/50 text-[10px] tracking-widest uppercase">Sarah Chen</div>
        <Wifi className="absolute top-4 right-4 w-5 h-5 text-coral rotate-45" />
        {/* NFC ripples at the corner */}
        {[0, 1, 2].map((i) => (
          <motion.span
            key={i}
            animate={{ opacity: [0, 0.7, 0, 0, 0, 0], scale: [0.3, 1 + i * 0.7, 2.6 + i * 0.5, 2.6, 2.6, 0.3] }}
            transition={LOOP}
            className="absolute top-6 right-6 w-6 h-6 rounded-full border border-coral"
          />
        ))}
      </motion.div>

      {/* The phone */}
      <motion.div
        animate={{ opacity: [0, 1, 1, 1, 1, 0], y: [-58, -58, 0, 0, 0, -58], rotate: [-4, -4, 0, 0, 0, -4] }}
        transition={LOOP}
        className="relative z-20 w-[168px] h-[344px] rounded-[34px] bg-[#c7c9cc] p-[3px] shadow-2xl"
      >
        <div className="relative w-full h-full rounded-[31px] bg-ink overflow-hidden">
          {/* dynamic island */}
          <div className="absolute top-2 left-1/2 -translate-x-1/2 w-16 h-4 bg-black rounded-full z-30" />

          {/* Agent card page fills the screen */}
          <motion.div
            animate={{ opacity: [0, 0, 0, 1, 1, 0] }}
            transition={LOOP}
            className="absolute inset-0 bg-bone flex flex-col"
          >
            <div className="h-16 bg-gradient-to-br from-coral to-[#ff7a5c]" />
            <div className="px-3 -mt-7">
              <img src={IMAGES.testimonial1} alt="Agent Sarah Chen" className="w-14 h-14 rounded-full object-cover border-2 border-bone" />
              <div className="mt-1.5 font-serif text-ink text-lg leading-none">Sarah Chen</div>
              <div className="text-ink/50 text-[9px]">Coldwell Banker · Austin</div>
              <div className="mt-3 grid grid-cols-3 gap-1">
                {LISTING_THUMBS.map((s) => (
                  <div key={s} className="aspect-square overflow-hidden rounded-md">
                    <img src={s} alt="Listing" className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
              <motion.div
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ duration: 1.1, repeat: Infinity, ease: "easeInOut" }}
                className="mt-3 flex items-center justify-center gap-1.5 bg-ink text-bone py-2 rounded-md"
              >
                <Bookmark className="w-3 h-3" />
                <span className="text-[10px] font-medium">Save Contact</span>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Success toast */}
      <motion.div
        animate={{ opacity: [0, 0, 0, 0, 1, 0], y: [14, 14, 14, 14, 0, 0] }}
        transition={LOOP}
        className="absolute bottom-0 z-30 flex items-center gap-2 bg-bone px-3.5 py-2 shadow-xl border border-ink/10"
      >
        <span className="w-4 h-4 rounded-full bg-green-500 flex items-center justify-center">
          <Check className="w-2.5 h-2.5 text-white" />
        </span>
        <span className="text-[11px] font-medium text-ink">Sarah Chen just tapped your card</span>
      </motion.div>
    </div>
  );
}

function ComparisonTable() {
  const rows = [
    ["Cost to reprint", "$200 / 1,000 forever", "One-time $29"],
    ["They keep it?", "Usually thrown out", "Saved to their contacts"],
    ["Track engagement?", "No", "Yes — every tap logged"],
    ["Update your info", "Reprint all cards", "Change once, everywhere"],
    ["Capture as lead", "Manual data entry", "Automatic"],
    ["Show your listings", "No", "Yes, live"],
    ["Show your virtual tour", "No", "Yes, one tap"],
    ["Chat with AI on your behalf", "No", "Yes, 24/7"],
  ];
  return (
    <div className="overflow-x-auto border border-ink/10" data-testid="nfc-comparison">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left">
            <th className="px-5 py-4 font-medium text-ink/50 text-xs uppercase tracking-[0.1em]"></th>
            <th className="px-5 py-4 font-medium text-ink/50 text-xs uppercase tracking-[0.1em]">Paper card</th>
            <th className="px-5 py-4 font-medium text-coral text-xs uppercase tracking-[0.1em] bg-ink/[0.03]">Chateaux NFC card</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([label, paper, chateaux]) => (
            <tr key={label} className="border-t border-ink/10">
              <td className="px-5 py-4 text-ink font-medium">{label}</td>
              <td className="px-5 py-4 text-ink/50">{paper}</td>
              <td className="px-5 py-4 text-ink bg-ink/[0.03]">{chateaux}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export const NfcCard = () => {
  const [taps, setTaps] = useState(12480);
  const { openLead } = useLead();

  useEffect(() => {
    axios.get(`${API}/public/stats/card-taps`).then((r) => setTaps(r.data.taps)).catch(() => {});
  }, []);

  const HOW = [
    { n: "01", title: "We ship you the card.", body: "Physical card, laser-etched with your name and Chateaux logo. Real NFC chip embedded. Ships free with any annual plan." },
    { n: "02", title: "You tap. They save.", body: "Any phone taps the card → opens your card.chateaux.ai/c/{yourname} page instantly. No app download. No signup." },
    { n: "03", title: "They become a lead.", body: "The moment someone taps \u201CSave contact\u201D or \u201CChat with AI\u201D, they land in your Leads inbox with source card_tap and full attribution." },
  ];

  return (
    <section id="nfc-card" className="border-t border-ink/10" data-testid="nfc-section">
      {/* Above the fold: positioning + tap sim */}
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-8 items-center">
          <div className="lg:col-span-6">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.2em] text-coral mb-4">NFC business card</p>
              <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.0] tracking-tight text-ink">
                Stop handing out paper cards. Tap your phone. They keep everything.
              </h2>
              <p className="mt-6 max-w-xl text-base md:text-lg text-ink/70 leading-relaxed">
                Your NFC card unlocks your full digital profile — photo, listings, calendar link,
                virtual tour, and a private chat with your AI concierge. The moment they tap,
                they&apos;re a warm lead in your Chateaux inbox.
              </p>

              <div className="mt-9 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
                {CAPTIONS.map((c) => (
                  <div key={c.text} className="flex items-center gap-3">
                    <span className="w-8 h-8 shrink-0 flex items-center justify-center border border-ink/15">
                      <c.icon className="w-4 h-4 text-coral" />
                    </span>
                    <span className="text-sm text-ink/75">{c.text}</span>
                  </div>
                ))}
              </div>

              <button
                onClick={() => openLead({ source: "nfc-card" })}
                data-testid="nfc-cta"
                className="group mt-10 inline-flex items-center gap-2 bg-coral text-bone px-7 py-4 text-sm font-medium hover:bg-ink transition-colors duration-300"
              >
                Claim your card
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </button>
            </Reveal>
          </div>

          <div className="lg:col-span-6 flex justify-center">
            <Reveal delay={0.1}>
              <TapSimulation />
            </Reveal>
          </div>
        </div>
      </div>

      {/* Feel it live — QR */}
      <div className="bg-ink text-bone py-24" data-testid="nfc-feel-it-live">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.2em] text-coral mb-4">Try the receiver side</p>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight">Feel it live.</h2>
            <p className="mt-6 max-w-md text-base md:text-lg text-bone/70 leading-relaxed">
              Scan this QR with your phone camera. You&apos;ll land on a real Chateaux agent card —
              the same page a lead sees after tapping.
            </p>
            <p className="mt-4 text-sm text-bone/50">
              On mobile?{" "}
              <a href={DEMO_CARD_URL} target="_blank" rel="noreferrer" className="text-coral underline underline-offset-2" data-testid="nfc-demo-link">
                Tap here instead
              </a>
              .
            </p>
          </Reveal>
          <Reveal delay={0.1} className="flex justify-center lg:justify-end">
            <div className="bg-bone p-5 shadow-2xl">
              <QRCodeSVG value={DEMO_CARD_URL} size={200} fgColor="#0B0B0F" bgColor="#F5F3EE" level="M" />
            </div>
          </Reveal>
        </div>
      </div>

      {/* How it works */}
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 py-24 md:py-32">
        <Reveal>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-ink max-w-3xl">
            How the card works.
          </h2>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-px bg-ink/10 border border-ink/10">
          {HOW.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.1}>
              <div className="h-full bg-bone p-8 md:p-10">
                <div className="font-serif text-6xl text-coral leading-none">{s.n}</div>
                <h3 className="mt-5 font-serif text-2xl md:text-3xl text-ink">{s.title}</h3>
                <p className="mt-3 text-sm text-ink/60 leading-relaxed">{s.body}</p>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Comparison */}
        <Reveal delay={0.1}>
          <h3 className="mt-20 mb-6 font-serif text-3xl md:text-4xl text-ink">Paper vs. Chateaux.</h3>
          <ComparisonTable />
        </Reveal>

        {/* Trust bar */}
        <Reveal delay={0.15}>
          <p className="mt-12 text-center text-sm md:text-base text-ink/60" data-testid="nfc-trust-bar">
            Chateaux cards have been tapped{" "}
            <span className="text-ink font-medium">
              {taps.toLocaleString()}
            </span>{" "}
            times this month by real buyers.
          </p>
        </Reveal>
      </div>
    </section>
  );
};
